import React, { useCallback, useEffect, useState } from "react";
import testimonials from "@content/testimonials.json";

const students = testimonials.students || [];
const screenshots = testimonials.whatsapp_screenshots || [];

const Testimonials = () => {
    const [openIdx, setOpenIdx] = useState(null);

    const close = useCallback(() => setOpenIdx(null), []);
    const prev = useCallback(
        () => setOpenIdx((i) => (i === null ? null : (i - 1 + screenshots.length) % screenshots.length)),
        []
    );
    const next = useCallback(
        () => setOpenIdx((i) => (i === null ? null : (i + 1) % screenshots.length)),
        []
    );

    useEffect(() => {
        if (openIdx === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [openIdx, close, prev, next]);

    return (
        <>
            <section className="section" id="testimonials" data-testid="testimonials-section">
                <div className="container-p">
                    <div className="max-w-3xl">
                        <span className="eyebrow">Students Just Like You</span>
                        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
                            They Were Stuck Too.{" "}
                            <span className="text-brand">Look at Them Now.</span>
                        </h2>
                        <p className="mt-4 text-dim text-base md:text-lg">
                            Real Desktop Support and Helpdesk engineers who stopped waiting — and finally
                            built the networking career they always wanted.
                        </p>
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {students.map((t, i) => (
                            <article
                                key={i}
                                className="panel panel-hover p-5 relative"
                                data-testid={`testimonial-${i + 1}`}
                            >
                                <div className="inline-flex items-center gap-2 font-mono text-[11px] px-2 py-1 rounded border border-[rgba(124,231,59,0.35)] bg-[rgba(124,231,59,0.06)] text-brand">
                                    <span className="pulse-dot" style={{ width: 6, height: 6 }} />
                                    {t.tag}
                                </div>
                                <p className="mt-4 text-[15px] leading-relaxed text-slate-900">
                                    &ldquo;{t.text}&rdquo;
                                </p>
                                <div className="mt-5 flex items-center gap-3 pt-4 border-t border-[var(--border)]">
                                    <img
                                        src={t.image}
                                        alt={`Photo of ${t.name}, Grads2Pro student from ${t.company}`}
                                        width="48"
                                        height="48"
                                        className="w-12 h-12 rounded-full object-cover border border-[var(--border)]"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <div>
                                        <div className="font-bold text-slate-900 text-sm">
                                            {t.name}{" "}
                                            <span className="text-dim font-normal">· {t.company}</span>
                                        </div>
                                        <div className="font-mono text-[11px] text-brand tracking-widest uppercase">
                                            Grads2Pro Trained
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {screenshots.length > 0 && (
                <section
                    className="section pt-0"
                    id="student-messages"
                    data-testid="student-messages-section"
                    aria-labelledby="student-messages-heading"
                >
                    <div className="container-p">
                        <div className="max-w-3xl">
                            <span className="eyebrow gray">Verified Screenshots</span>
                            <h2
                                id="student-messages-heading"
                                className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900"
                            >
                                What Students <span className="text-brand">Actually Said.</span>
                            </h2>
                            <p className="mt-4 text-dim text-base md:text-lg">
                                Unedited messages from students who reached out after the workshop.
                                Tap any screenshot to view it full size.
                            </p>
                        </div>

                        <ul
                            className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                            role="list"
                            data-testid="student-messages-gallery"
                        >
                            {screenshots.map((s, i) => (
                                <li key={s.src + i}>
                                    <button
                                        type="button"
                                        onClick={() => setOpenIdx(i)}
                                        className="panel panel-hover overflow-hidden text-left group w-full h-full"
                                        aria-label={`Enlarge screenshot: ${s.caption || s.alt}`}
                                        data-testid={`chat-${i + 1}`}
                                    >
                                        <div className="relative">
                                            <img
                                                src={s.src}
                                                alt={s.alt}
                                                width="640"
                                                height="800"
                                                className="w-full h-64 sm:h-72 object-cover object-top border-b border-[var(--border)] transition-transform duration-300 group-hover:scale-[1.02]"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                            <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-white/90 border border-[var(--border)] text-brand">
                                                ● Verified
                                            </span>
                                            <span className="absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-white/90 border border-[var(--border)] text-slate-700">
                                                Tap to zoom
                                            </span>
                                        </div>
                                        {s.caption && (
                                            <div className="p-4">
                                                <div className="font-mono text-xs text-brand tracking-widest uppercase">
                                                    {s.caption}
                                                </div>
                                            </div>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {openIdx !== null && (
                        <div
                            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Student message screenshot viewer"
                            onClick={close}
                            data-testid="chat-lightbox"
                        >
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    close();
                                }}
                                aria-label="Close viewer"
                                className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/30 bg-black/60 text-white hover:border-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                                data-testid="chat-lightbox-close"
                            >
                                ✕
                            </button>

                            {screenshots.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            prev();
                                        }}
                                        aria-label="Previous screenshot"
                                        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/30 bg-black/60 text-white hover:border-brand"
                                        data-testid="chat-lightbox-prev"
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            next();
                                        }}
                                        aria-label="Next screenshot"
                                        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/30 bg-black/60 text-white hover:border-brand"
                                        data-testid="chat-lightbox-next"
                                    >
                                        ›
                                    </button>
                                </>
                            )}

                            <figure
                                className="flex flex-col items-center gap-3 max-w-[95vw]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <img
                                    src={screenshots[openIdx].src}
                                    alt={screenshots[openIdx].alt}
                                    className="max-w-full max-h-[80vh] rounded-lg border border-white/10 shadow-2xl"
                                    loading="eager"
                                    decoding="async"
                                />
                                {screenshots[openIdx].caption && (
                                    <figcaption className="font-mono text-xs sm:text-sm text-white/85 tracking-wide text-center px-4">
                                        {screenshots[openIdx].caption}{" "}
                                        <span className="text-white/50">
                                            · {openIdx + 1} / {screenshots.length}
                                        </span>
                                    </figcaption>
                                )}
                            </figure>
                        </div>
                    )}
                </section>
            )}
        </>
    );
};

export default Testimonials;
